import React, { useState, useEffect, useContext } from 'react'
import AppLayout from '../layouts/AppLayout'
import { ALLOWLIST_TAGS } from '../constants/domain'
import { AppContext } from '../contexts/AppContext'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { FiPlus, FiDownload, FiSearch, FiX, FiEdit } from 'react-icons/fi'
import Swal from 'sweetalert2'

const AllowListedAccounts = () => {
  const {
    getAllowListedAccounts,
    addAllowListedAccounts,
    updateAllowListedAccount,
    deleteAllowListedAccount,
    exportAllowListedAccounts,
    isLoading,
  } = useContext(AppContext)

  const [accounts, setAccounts] = useState([])
  const [pagination, setPagination] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)

  // Form state
  const [urls, setUrls] = useState('')
  const [licFrom, setLicFrom] = useState('')
  const [licTo, setLicTo] = useState('')
  const [tag, setTag] = useState('')

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
      setCurrentPage(1) // Reset to first page when search changes
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  useEffect(() => {
    fetchAccounts(currentPage, itemsPerPage, debouncedSearchQuery)
  }, [currentPage, itemsPerPage, debouncedSearchQuery])

  const fetchAccounts = async (page = 1, limit = 10, search = '') => {
    try {
      const data = await getAllowListedAccounts(page, limit, search)
      setAccounts(data.accounts)
      setPagination(data.pagination)
    } catch (error) {
      console.error('Failed to fetch allow listed accounts:', error)
    }
  }

  const handleAddClick = () => {
    setIsEditMode(false)
    setEditingAccount(null)
    setUrls('')
    setLicFrom('')
    setLicTo('')
    setTag('')
    setIsModalOpen(true)
  }

  const handleEditClick = (account) => {
    setIsEditMode(true)
    setEditingAccount(account)
    setUrls(account.url)

    const formatDateForInput = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    setLicFrom(formatDateForInput(account.lic_from))
    setLicTo(formatDateForInput(account.lic_to))
    setTag(account.tag || '')
    setIsModalOpen(true)
  }

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure to delete?',
        text: "You won't be able to undo this action",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#E51A4D',
        cancelButtonColor: '#2F2F2F',
        confirmButtonText: 'Yes, Delete',
      })

      if (result.isConfirmed) {
        await deleteAllowListedAccount(id)
        fetchAccounts(currentPage, itemsPerPage, debouncedSearchQuery)
      }
    } catch (error) {
      Swal.fire('An Error Occurred', error.message, 'error')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isEditMode) {
      const result = await updateAllowListedAccount({
        id: editingAccount.id,
        url: urls,
        lic_from: licFrom || null,
        lic_to: licTo || null,
        tag: tag || null,
      })

      if (result) {
        setIsModalOpen(false)
        fetchAccounts(currentPage, itemsPerPage, debouncedSearchQuery)
      }
    } else {
      const urlArray = urls
        .split('\n')
        .map((url) => url.trim())
        .filter((url) => url.length > 0)

      if (urlArray.length === 0) {
        Swal.fire('Error', 'Please enter at least one URL', 'error')
        return
      }

      const result = await addAllowListedAccounts({
        urls: urlArray,
        lic_from: licFrom || null,
        lic_to: licTo || null,
        tag: tag || null,
      })

      if (result) {
        setIsModalOpen(false)
        setUrls('')
        setLicFrom('')
        setLicTo('')
        setTag('')
        fetchAccounts(1, itemsPerPage, debouncedSearchQuery)
      }
    }
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit)
    setCurrentPage(1)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <AppLayout pageName='Allowlist'>
      <div className='max-w-[95%] mx-auto p-6 space-y-6'>
        <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
          <div>
            <h1 className='text-3xl font-bold text-gray-900'>Allowlist</h1>
            <p className='text-sm text-gray-500 mt-1'>Manage whitelisted accounts and domains</p>
          </div>
          <div className='flex items-center gap-3 flex-wrap'>
            <button
              onClick={handleAddClick}
              className='flex items-center gap-2 px-5 py-2.5 btn-main-inverse text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md'
            >
              <FiPlus size={18} />
              Add Account
            </button>
            <button
              onClick={exportAllowListedAccounts}
              className='flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm hover:shadow-md'
            >
              <FiDownload size={18} />
              Export CSV
            </button>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <div className='flex-1'>
            <div className='relative'>
              <FiSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' size={18} />
              <input
                type='text'
                placeholder='Search by URL, platform, or tag...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
              />
            </div>
          </div>
        </div>

        <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
          {isLoading ? (
            <div className='text-center py-16'>
              <div className='animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto'></div>
              <p className='mt-3 text-gray-600 font-medium'>Loading accounts...</p>
            </div>
          ) : accounts.length > 0 ? (
            <>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Platform
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        URL
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Licensed From
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Licensed To
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Tag
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Added at
                      </th>
                      <th className='px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-200'>
                    {accounts.map((account) => (
                      <tr key={account.id} className='hover:bg-gray-50 transition-colors'>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          {account.platform && (
                            <div className='flex items-center gap-2'>
                              <img
                                width='20'
                                height='20'
                                src={`/images/icons/${account.platform}.png`}
                                alt={account.platform}
                                className='object-contain'
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                }}
                              />
                              <span className='text-sm font-medium text-gray-900 capitalize'>{account.platform}</span>
                            </div>
                          )}
                          {!account.platform && <span className='text-sm text-gray-400'>-</span>}
                        </td>
                        <td className='px-6 py-4'>
                          <a
                            href={account.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-blue-600 hover:text-blue-800 hover:underline break-all text-sm font-medium'
                          >
                            {account.url}
                          </a>
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                          {account.lic_from ? formatDate(account.lic_from) : <span className='text-gray-400'>-</span>}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>
                          {account.lic_to ? formatDate(account.lic_to) : <span className='text-gray-400'>-</span>}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          {account.tag ? (
                            <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                              {account.tag}
                            </span>
                          ) : (
                            <span className='text-sm text-gray-400'>-</span>
                          )}
                        </td>
                        <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-600'>{formatDate(account.created_at)}</td>
                        <td className='px-6 py-4 whitespace-nowrap'>
                          <div className='flex items-center gap-3'>
                            <button
                              onClick={() => handleEditClick(account)}
                              className='p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors'
                              title='Edit'
                            >
                              <FiEdit size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(account.id)}
                              className='p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors'
                              title='Delete'
                            >
                              <FiX size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages && pagination.totalPages > 1 && (
                <div className='bg-gray-50 border-t border-gray-200 px-6 py-4'>
                  <div className='flex flex-col sm:flex-row justify-between items-center gap-4'>
                    <div className='flex items-center gap-4 flex-wrap'>
                      <div className='flex items-center gap-2'>
                        <label htmlFor='itemsPerPage' className='text-sm font-medium text-gray-700'>
                          Rows per page:
                        </label>
                        <select
                          id='itemsPerPage'
                          value={itemsPerPage}
                          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                          className='border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white'
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>
                      <div className='text-sm text-gray-600'>
                        <span className='font-semibold text-gray-700'>{pagination.totalItems}</span> total records
                      </div>
                    </div>

                    <div className='flex items-center gap-3'>
                      <div className='text-sm text-gray-600'>
                        Page <span className='font-semibold text-gray-700'>{pagination.currentPage || 1}</span> of{' '}
                        <span className='font-semibold text-gray-700'>{pagination.totalPages || 1}</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <button
                          onClick={() => handlePageChange((pagination.currentPage || 1) - 1)}
                          disabled={!pagination.hasPrevPage}
                          className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:border-gray-400 transition-all bg-white'
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange((pagination.currentPage || 1) + 1)}
                          disabled={!pagination.hasNextPage}
                          className='px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:border-gray-400 transition-all bg-white'
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className='text-center py-16'>
              <div className='text-gray-400 mb-2'>
                <FiSearch size={48} className='mx-auto opacity-50' />
              </div>
              <p className='text-gray-500 font-medium'>No allow listed accounts found</p>
              <p className='text-sm text-gray-400 mt-1'>{searchQuery ? 'Try adjusting your search' : 'Get started by adding your first account'}</p>
            </div>
          )}
        </div>

        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as='div' className='relative z-50' onClose={() => setIsModalOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 hidden bg-gray-500 bg-opacity-25 blur-xl transition-opacity md:block' />
            </Transition.Child>

            <div className='fixed inset-0 overflow-y-auto'>
              <div className='flex min-h-full items-center justify-center p-4 text-center'>
                <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 scale-95'
                  enterTo='opacity-100 scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 scale-100'
                  leaveTo='opacity-0 scale-95'
                >
                  <Dialog.Panel className='w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-8 text-left align-middle shadow-xl transition-all'>
                    <div className='flex items-center justify-between mb-6'>
                      <Dialog.Title as='h3' className='text-xl font-semibold leading-6 text-gray-900'>
                        {isEditMode ? 'Edit Allow Listed Account' : 'Add Allow Listed Accounts'}
                      </Dialog.Title>
                      <button onClick={() => setIsModalOpen(false)} className='text-gray-400 hover:text-gray-600 transition-colors'>
                        <FiX size={20} />
                      </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                      <div className='space-y-5'>
                        <div>
                          <label className='block text-sm font-semibold text-gray-700 mb-2'>
                            {isEditMode ? 'URL' : 'URLs (one per line)'}
                          </label>
                          {isEditMode ? (
                            <input
                              type='text'
                              value={urls}
                              onChange={(e) => setUrls(e.target.value)}
                              className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                              required
                            />
                          ) : (
                            <textarea
                              value={urls}
                              onChange={(e) => setUrls(e.target.value)}
                              rows={5}
                              className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none'
                              placeholder='Enter URLs, one per line'
                              required
                            />
                          )}
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <label className='block text-sm font-semibold text-gray-700 mb-2'>Licensed From</label>
                            <input
                              type='date'
                              value={licFrom}
                              onChange={(e) => setLicFrom(e.target.value)}
                              className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                            />
                          </div>
                          <div>
                            <label className='block text-sm font-semibold text-gray-700 mb-2'>Licensed To</label>
                            <input
                              type='date'
                              value={licTo}
                              onChange={(e) => setLicTo(e.target.value)}
                              className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all'
                            />
                          </div>
                        </div>

                        <div>
                          <label className='block text-sm font-semibold text-gray-700 mb-2'>Tag</label>
                          <select
                            value={tag}
                            onChange={(e) => setTag(e.target.value)}
                            className='w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white'
                          >
                            <option value=''>Select a tag</option>
                            {ALLOWLIST_TAGS.map((t) => (
                              <option
                                key={t.value}
                                value={t.value}
                              >
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className='mt-8 flex justify-end gap-3 pt-6 border-t border-gray-200'>
                        <button
                          type='button'
                          onClick={() => setIsModalOpen(false)}
                          className='px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-all'
                        >
                          Cancel
                        </button>
                        <button
                          type='submit'
                          disabled={isLoading}
                          className='px-5 py-2.5 btn-main-inverse text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-sm hover:shadow-md'
                        >
                          {isLoading ? 'Saving...' : isEditMode ? 'Update Account' : 'Add Accounts'}
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </AppLayout>
  )
}

export default AllowListedAccounts

